import PropTypes from 'prop-types';
import compose from "ramda/es/compose";
import equals from "ramda/es/equals";
import filter from "ramda/es/filter";
import flatten from "ramda/es/flatten";
import groupBy from "ramda/es/groupBy";
import keys from "ramda/es/keys";
import map from "ramda/es/map";
import mapAccum from "ramda/es/mapAccum";
import prop from "ramda/es/prop";
import reverse from "ramda/es/reverse";
import sortBy from "ramda/es/sortBy";
import toPairs from "ramda/es/toPairs";
import when from "ramda/es/when";
import cx from "ramda-extension/es/cx";
import keyMirror from "ramda-extension/es/keyMirror";
import React, { Children, cloneElement, Component, createContext } from 'react';
import { withDefaultComponentProps } from '../../../decorators/withDefaultComponentProps';
const COMPONENT_NAME = 'Table';
export const TableContext = /*#__PURE__*/ createContext({});
class Table extends Component {
    constructor(props){
        super(props);
        this.columnDescriptor = null;
        this._lastDescriptor = null;
        this._tableElement = null;
        // todo debounce here (not inside onWindowResized, as we want no delay on first render cycle)
        window.addEventListener('resize', this.onWindowResized);
    }
    componentDidMount() {
        this.onWindowResized();
    }
    componentWillUnmount() {
        window.removeEventListener('resize', this.onWindowResized);
    }
    registerHeader = (descriptor, isLast)=>{
        // save only if greater number of columns
        if (!this.columnDescriptor || descriptor.cols > this.columnDescriptor.cols) {
            this.columnDescriptor = descriptor;
        }
        if (isLast && !equals(this.columnDescriptor)(this._lastDescriptor) && this.props.responsiveMode !== Table.responsiveModes.SCROLL) {
            this._lastDescriptor = this.columnDescriptor;
        }
    };
    doResponsiveWithColumnHiding = (tableWidth)=>{
        // this.state.columnDescriptor should be the same as this._lastDescriptor, but that's not the case on first
        // render when the component was mounted. We can not afford to show the table in "wrong" responsive state
        // at first, and on next render cycle correct it. So we need to "access state" before it was set.
        // FYI, at this point, call to setState was already done, but it will be propagated to this.state by React only
        // in next render cycle
        const descriptor = this._lastDescriptor;
        const widthWithoutHidden = descriptor.colSpec.filter((col)=>!col.visible).reduce((last, col)=>last - col.width, +descriptor.totalWidth);
        // see if we should downsize (hide columns)
        if (tableWidth < widthWithoutHidden) {
            // should scale down
            // flat array of descriptors sorted by priority and order
            const flattenedByPriorityAndOrder = compose(// map(val => val.priority + ' : ' + val.order), // uncomment to check sorting
            // forEach(val => console.log(val.priority + ' : ' + val.order)),
            flatten, map((val)=>val.cols), sortBy(prop('priority')), map((val)=>({
                    priority: val[0],
                    cols: val[1]
                })), toPairs, map(reverse), map(sortBy(prop('order'))), groupBy(prop('priority')))(descriptor.colSpec);
            mapAccum((acc, col)=>{
                if (acc > tableWidth) {
                    // this is reference to state object
                    // todo do not mutate state object
                    col.visible = false;
                    col.brokenAt = acc;
                }
                return [
                    acc - col.width,
                    col
                ];
            }, +descriptor.totalWidth, flattenedByPriorityAndOrder);
        } else {
            // see if we should upsize (show columns)
            // find columns, that are supposed to be shown
            const findColsToShow = filter((col)=>tableWidth > col.brokenAt);
            const showCols = findColsToShow(descriptor.colSpec);
            showCols.forEach((col)=>{
                col.visible = true;
                col.brokenAt = undefined;
            });
            if (showCols.length <= 0) {
                this._lastDescriptor = null;
            }
        }
    };
    onWindowResized = ()=>{
        if (this._tableElement) {
            const tableWidth = this._tableElement.getBoundingClientRect()?.width;
            when(()=>this.props.responsiveMode === Table.responsiveModes.HIDE, this.doResponsiveWithColumnHiding, tableWidth);
            this.setState(()=>({
                    realClientWidth: tableWidth
                }));
        }
    };
    render() {
        const { className, children, hiddenHeader, responsiveMode, typeChild, ...other } = this.props;
        const addDescriptor = map((child)=>/*#__PURE__*/ cloneElement(child, {
                columnDescriptor: this._lastDescriptor
            }));
        const hideHeader = when(()=>hiddenHeader, map((child)=>child.props.isHeader ? /*#__PURE__*/ cloneElement(child, {
                hiddenHeader
            }) : child));
        const scrollableTable = this._lastDescriptor && responsiveMode === Table.responsiveModes.SCROLL && this.state.realClientWidth < this._lastDescriptor.totalWidth;
        return /*#__PURE__*/ React.createElement(TableContext.Provider, {
            value: {
                registerHeader: this.registerHeader
            }
        }, /*#__PURE__*/ React.createElement("div", null, /*#__PURE__*/ React.createElement("table", {
            ref: (el)=>{
                this._tableElement = el;
            },
            className: cx('Table', {
                'Table--noHeader': hiddenHeader,
                'Table--typeScrollable': scrollableTable,
                'Table--typeChild': typeChild
            }, className),
            ...other
        }, compose(hideHeader, addDescriptor, Children.toArray)(children))));
    }
}
Table.responsiveModes = keyMirror({
    SCROLL: null,
    HIDE: null
});
Table.propTypes = {
    /**
	 * Children will be rendered within the `Table`.
	 */ children: PropTypes.node,
    /**
	 * Custom `className` for the root.
	 */ className: PropTypes.string,
    /**
	 * TableHeader must be defined because of responsive behaviour. But when you want to hide header, use this prop.
	 */ hiddenHeader: PropTypes.bool,
    /**
	 * How table responsiveness will be solved. Table.responsiveModes SCROLL or HIDE.
	 */ responsiveMode: PropTypes.oneOf(keys(Table.responsiveModes)),
    /**
	 * Table will have `Table--typeChild` class.
	 */ typeChild: PropTypes.bool
};
Table.displayName = 'Table';
export { Table };
export default withDefaultComponentProps(COMPONENT_NAME, {
    responsiveMode: Table.responsiveModes.SCROLL
}, Table);
